/**
 * Cloud-mode store. Talks to the `/v1/orgs/:slug/...` endpoints on the
 * Bliksys API. All requests go with `credentials: 'include'` so the
 * session cookie set by sign-in flows through.
 *
 * Server <-> client shape adaptation:
 *   • Server returns `schemaJson` / `contentJson` / `version` / etc.
 *     We map to the editor's `schema` / `data` / `version` shape so the
 *     existing UI keeps working unchanged.
 *   • Listings include `deployTargetKind` so the recent-projects badge
 *     can render without a per-project follow-up fetch — we batch one
 *     extra GET to `/deploy-targets` and join client-side.
 *   • Project details intentionally omit the inline `deployTarget`
 *     config: cloud editing shouldn't expose secret fields the user
 *     never typed in. The Deploy button instead calls
 *     `/v1/orgs/:slug/projects/:id/deploy`, which decrypts the
 *     credentials server-side.
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';

import {
  type Project,
  type ProjectCapabilities,
  type ProjectStore,
  type ProjectSummary,
  UnauthenticatedError,
  VersionConflictError,
} from './types.js';

export interface ApiStoreOptions {
  /** Base URL of the API, e.g. `https://api.bliksys.com`. */
  baseUrl: string;
  /** Slug of the active organization, e.g. `acme`. */
  orgSlug: string;
}

export function createApiStore(opts: ApiStoreOptions): ProjectStore {
  const orgPath = `${opts.baseUrl}/v1/orgs/${encodeURIComponent(opts.orgSlug)}`;

  return {
    mode: 'cloud',
    orgSlug: opts.orgSlug,

    async list(): Promise<ProjectSummary[]> {
      // Two fetches in parallel — projects listing + deploy-targets so
      // we can render the per-row "kind" badge without N+1 round trips.
      const [projectsRes, targetsRes] = await Promise.all([
        request(`${orgPath}/projects`),
        request(`${orgPath}/deploy-targets`),
      ]);
      const projects = (await projectsRes.json()) as { projects: ServerProjectListRow[] };
      const targets = (await targetsRes.json()) as {
        deployTargets: { id: string; kind: string }[];
      };
      const kindById = new Map(targets.deployTargets.map((t) => [t.id, t.kind]));

      return projects.projects.map((p) => {
        const summary: ProjectSummary = {
          id: p.id,
          name: p.name,
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
        };
        const kind = p.deployTargetId ? kindById.get(p.deployTargetId) : undefined;
        if (isDeployKind(kind)) summary.deployTargetKind = kind;
        return summary;
      });
    },

    async get(id: string): Promise<Project | null> {
      const res = await request(`${orgPath}/projects/${encodeURIComponent(id)}`, {
        allow404: true,
      });
      if (res.status === 404) return null;
      const body = (await res.json()) as { project: ServerProjectFull };
      return adaptProject(body.project);
    },

    async getCapabilities(id: string): Promise<ProjectCapabilities> {
      const res = await request(`${orgPath}/projects/${encodeURIComponent(id)}`);
      const body = (await res.json()) as { capabilities: ProjectCapabilities };
      return body.capabilities;
    },

    async save(project: Project): Promise<Project> {
      // Save semantics:
      //   • Project has a server-side id and version → PATCH (optimistic concurrency).
      //   • Project lacks server-side state → POST (create).
      // The editor reuses Project rows it got from `get`/`list`, so the
      // common case is PATCH. Create is used only by the import flow
      // and the "new project" button.
      const isCreate = project.version === undefined;
      if (isCreate) {
        const res = await request(`${orgPath}/projects`, {
          method: 'POST',
          body: {
            name: project.name,
            schema: project.schema,
            data: project.data,
          },
        });
        const body = (await res.json()) as { project: ServerProjectFull };
        return adaptProject(body.project);
      }

      try {
        const res = await request(`${orgPath}/projects/${encodeURIComponent(project.id)}`, {
          method: 'PATCH',
          body: {
            expectedVersion: project.version,
            name: project.name,
            schema: project.schema,
            data: project.data,
            // null is meaningful (clears the link); undefined means
            // "don't touch". Only forward when the caller passed a
            // value so the server's defaults aren't overwritten.
            ...(project.deployTargetId !== undefined && {
              deployTargetId: project.deployTargetId,
            }),
            ...(project.assetOriginId !== undefined && {
              assetOriginId: project.assetOriginId,
            }),
          },
        });
        const body = (await res.json()) as { project: ServerProjectFull };
        return adaptProject(body.project);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'VERSION_CONFLICT') {
          const details = err.details as
            | { actualVersion?: number; expectedVersion?: number }
            | undefined;
          throw new VersionConflictError('Project was edited elsewhere', {
            actualVersion: details?.actualVersion ?? -1,
            expectedVersion: details?.expectedVersion ?? project.version ?? -1,
          });
        }
        throw err;
      }
    },

    async delete(id: string): Promise<void> {
      await request(`${orgPath}/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },
  };
}

/* ---------------- shape adapters ---------------- */

interface ServerProjectListRow {
  id: string;
  name: string;
  version: number;
  deployTargetId: string | null;
  assetOriginId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ServerProjectFull extends ServerProjectListRow {
  schemaJson: JsonSchema;
  contentJson: JsonValue;
  orgId: string;
}

function adaptProject(p: ServerProjectFull): Project {
  return {
    id: p.id,
    name: p.name,
    schema: p.schemaJson,
    data: p.contentJson,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    version: p.version,
    deployTargetId: p.deployTargetId,
    assetOriginId: p.assetOriginId,
    // Cloud-mode projects deliberately don't surface inline deploy/asset
    // configs — those live server-side and the deploy goes through the
    // paid endpoint, which decrypts the secrets internally.
  };
}

function isDeployKind(s: string | undefined): s is 'github' | 'webhook' | 'cloudflareKV' {
  return s === 'github' || s === 'webhook' || s === 'cloudflareKV';
}

/* ---------------- low-level HTTP ---------------- */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** When true, a 404 returns the Response instead of throwing. Used by
   * `get` so callers can distinguish "not found" from a real error. */
  allow404?: boolean;
}

class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(args: { status: number; code: string; message: string; details: unknown }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.details = args.details;
  }
}

async function request(url: string, opts: RequestOptions = {}): Promise<Response> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    ...(opts.body !== undefined && {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts.body),
    }),
  };
  const res = await fetch(url, init);
  if (res.status === 401) {
    throw new UnauthenticatedError();
  }
  if (res.ok) return res;
  if (opts.allow404 && res.status === 404) return res;

  // Try to parse the canonical { code, message, details? } shape.
  let body: { code?: string; message?: string; details?: unknown } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* fall through */
  }
  throw new ApiError({
    status: res.status,
    code: body.code ?? 'HTTP_ERROR',
    message: body.message ?? res.statusText,
    details: body.details,
  });
}

// Exported for tests — the runtime path doesn't construct ApiError
// directly, but tests assert on the typed error shape.
export { ApiError };
